import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { userService, authService } from '../../services/firebaseService';

const SimpleSubscriptionPlans = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();

  // Plans d'abonnement disponibles
  const plans = [
    {
      id: 'free',
      name: 'Gratuit',
      price: 0,
      description: 'Accès limité aux fonctionnalités de base',
      features: [
        '1 tournoi maximum',
        '5 athlètes maximum',
        'Génération de matchs basique'
      ],
      tournamentsLimit: 1,
      athletesLimit: 5,
      color: 'light'
    },
    {
      id: 'basic',
      name: 'Basic',
      price: 9.99,
      description: 'Idéal pour les petits clubs',
      features: [
        '3 tournois maximum',
        '20 athlètes maximum',
        'Génération de matchs avancée',
        'Exportation des résultats'
      ],
      tournamentsLimit: 3,
      athletesLimit: 20,
      color: 'info'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 19.99,
      description: 'Pour les clubs de taille moyenne',
      features: [
        '10 tournois maximum',
        '100 athlètes maximum',
        'Toutes les fonctionnalités avancées',
        'Support prioritaire'
      ],
      tournamentsLimit: 10,
      athletesLimit: 100,
      color: 'primary'
    },
    {
      id: 'gala',
      name: 'Gala',
      price: 24.99,
      description: 'Idéal pour un gala ou tournoi de week-end',
      features: [
        '2 tournois maximum',
        '150 athlètes maximum',
        'Accès pendant 7 jours',
        'Génération de brackets optimisée',
        'Export PDF et Excel des résultats',
        'Support technique prioritaire'
      ],
      tournamentsLimit: 2,
      athletesLimit: 150,
      color: 'success',
      isOneTime: true,
      duration: '7 jours'
    }
  ];

  // Récupérer le profil utilisateur
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        
        // Vérifier si l'utilisateur est connecté
        const user = authService.getCurrentUser();
        if (!user) {
          // Rediriger vers la page d'accueil au lieu de la page de connexion
          navigate('/');
          return;
        }
        
        // Récupérer le profil utilisateur
        const profileData = await userService.getUserProfile();
        setUserProfile(profileData.user);
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération du profil utilisateur:', err);
        setError('Impossible de charger votre profil. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  // Gérer la sélection d'un plan
  const handleSelectPlan = async (planId) => {
    try {
      setLoading(true);
      
      // Si l'utilisateur sélectionne le plan gratuit, mettre à jour directement
      if (planId === 'free') {
        await userService.updateSubscription(planId);
        
        // Mettre à jour le profil utilisateur
        const profileData = await userService.getUserProfile();
        setUserProfile(profileData.user);
        
        setError(null);
        setLoading(false);
        return;
      }
      
      // Pour les plans payants (Gala, Basic, Premium), rediriger directement vers le lien Stripe
      if (['gala', 'basic', 'premium'].includes(planId)) {
        // Vérifier si l'utilisateur est connecté
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          navigate('/home');
          return;
        }

        // Obtenir l'email de l'utilisateur pour pré-remplir dans Stripe
        const userEmail = currentUser.email || '';
        
        // URL de base de l'application
        const appUrl = window.location.origin;
        
        // Définir les liens Stripe pour chaque plan
        const stripeLinks = {
          'gala': 'https://buy.stripe.com/00weV5cq389tdMQ79xcQU00', // Lien réel pour Gala
          'basic': 'https://buy.stripe.com/6oU00b3TxcpJ104dxVcQU02', // Lien réel pour Basic
          'premium': 'https://buy.stripe.com/cNiaEP0Hl0H1aAE65tcQU03' // Lien réel pour Premium
        };
        
        // Stocker le type de plan et les informations utilisateur dans le localStorage pour la récupération après redirection
        localStorage.setItem('selectedPlan', planId);
        localStorage.setItem('paymentInitiated', new Date().toISOString());
        
        // Stocker l'ID utilisateur pour réauthentification après redirection Stripe
        if (currentUser && currentUser.uid) {
          localStorage.setItem('stripe_auth_user_id', currentUser.uid);
          console.log('ID utilisateur sauvegardé pour réauthentification:', currentUser.uid);
        }
        
        // Construire le lien complet avec les paramètres de redirection simplifiés
        // Utiliser un minimum de paramètres pour éviter les erreurs
        const stripeUrl = `${stripeLinks[planId]}?prefilled_email=${encodeURIComponent(userEmail)}`;
        
        console.log(`Redirection vers Stripe pour le plan ${planId} avec le lien:`, stripeUrl);
        
        // Rediriger vers Stripe pour le paiement
        if (stripeUrl) {
          window.location.href = stripeUrl;
        }
        return;
      }
      
      // Pour les autres plans payants (si ajoutés à l'avenir), rediriger vers la page de paiement
      navigate(`/checkout?type=subscription&plan=${planId}`);
    } catch (err) {
      console.error('Erreur lors de la sélection du plan:', err);
      setError(err.message || 'Impossible de sélectionner ce plan. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Vérifier si un plan est le plan actuel de l'utilisateur
  const isCurrentPlan = (planId) => {
    return userProfile && userProfile.subscriptionType === planId;
  };

  if (loading && !userProfile) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h1 className="text-center mb-5">Plans d'abonnement</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {userProfile && (
        <Alert variant="info" className="mb-4">
          <strong>Votre abonnement actuel :</strong> {plans.find(p => p.id === userProfile.subscriptionType)?.name || 'Gratuit'}
          <br />
          <strong>Tournois créés :</strong> {userProfile.tournamentsCreated || 0} / {userProfile.tournamentsLimit || 1}
          <br />
          <strong>Athlètes maximum :</strong> {userProfile.athletesLimit || 5}
          <br />
          {userProfile.subscriptionEndDate && (
            <span>
              <strong>Date d'expiration :</strong> {userProfile.formattedEndDate || 'Non spécifiée'}
              <br />
              <strong>Statut :</strong> {userProfile.isSubscriptionActive ? 
                <span className="text-success">Actif</span> : 
                <span className="text-danger">Expiré</span>}
            </span>
          )}
        </Alert>
      )}
      
      <Row className="mb-5">
        {plans.map((plan) => (
          <Col key={plan.id} md={6} lg={3} className="mb-4">
            <Card className={`h-100 ${isCurrentPlan(plan.id) ? 'border-primary' : ''}`}>
              <Card.Header className={`bg-${plan.color} ${plan.color === 'light' ? 'text-dark' : 'text-white'}`}>
                <h5 className="my-0 font-weight-normal">{plan.name}</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                <Card.Title className="pricing-card-title">
                  {plan.price === 0 ? 'Gratuit' : `${plan.price}€`}
                  {plan.price > 0 && !plan.isOneTime && <small className="text-muted"> / mois</small>}
                  {plan.isOneTime && <small className="text-muted"> / {plan.duration}</small>}
                </Card.Title>
                <Card.Text>{plan.description}</Card.Text>
                <ul className="list-unstyled mt-3 mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="mb-2">✓ {feature}</li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <Button 
                    variant={isCurrentPlan(plan.id) ? "outline-primary" : "primary"} 
                    className="w-100"
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={loading || isCurrentPlan(plan.id)}
                  >
                    {loading ? (
                      <Spinner 
                        as="span" 
                        animation="border" 
                        size="sm" 
                        role="status" 
                        aria-hidden="true" 
                      />
                    ) : isCurrentPlan(plan.id) ? (
                      'Plan actuel'
                    ) : (
                      'Sélectionner'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      
      <div className="text-center mt-4">
        <h4>Besoin d'aide pour choisir ?</h4>
        <p>Contactez-nous pour obtenir des conseils personnalisés sur le plan qui convient le mieux à vos besoins.</p>
        <Button variant="outline-secondary" onClick={() => navigate('/contact')}>
          Nous contacter
        </Button>
      </div>
    </Container>
  );
};

export default SimpleSubscriptionPlans;
